import { useEffect, useRef, useState } from 'react';

// Changing account/workspace/filter invalidates both late responses and any
// auth-refresh replay performed by the shared API client.
export function useBusinessResource(loader, dependencies) {
  const generation = useRef(0);
  const [version, setVersion] = useState(0);
  const [state, setState] = useState({ loading: true, value: null, error: null });
  useEffect(() => {
    const ticket = ++generation.current;
    const isRequestCurrent = () => generation.current === ticket;
    setState(current => ({ ...current, loading: true, error: null }));
    Promise.resolve().then(() => loader({ isRequestCurrent })).then(value => {
      if (isRequestCurrent()) setState({ value, loading: false, error: null });
    }).catch(error => { if (isRequestCurrent()) setState({ value: null, loading: false, error }); });
    return () => { generation.current++; };
  }, [...dependencies, version]);
  return { ...state, refresh: () => setVersion(value => value + 1) };
}

export function useBusinessList(loader, dependencies) {
  const generation = useRef(0), pending = useRef(false);
  const [version, setVersion] = useState(0);
  const [state, setState] = useState({ loading: true, items: [], next: null, error: null });
  const load = async (after, ticket) => {
    if (pending.current) return;
    pending.current = true;
    const isRequestCurrent = () => generation.current === ticket;
    setState(current => ({ ...current, loading: true, error: null }));
    try {
      const page = await loader(after, { isRequestCurrent });
      if (isRequestCurrent()) setState(current => ({ ...page, loading: false, error: null, next: page.next,
        items: after ? [...current.items, ...page.items.filter(row => !current.items.some(old => old.id === row.id))] : page.items }));
    } catch (error) {
      if (isRequestCurrent()) setState(current => ({ ...current, loading: false, error,
        ...([401, 403, 404].includes(error.status) ? { items: [], next: null } : {}) }));
    }
    finally { if (isRequestCurrent()) pending.current = false; }
  };
  useEffect(() => {
    const ticket = ++generation.current;
    pending.current = false; setState({ loading: true, items: [], next: null, error: null });
    load(null, ticket);
    return () => { generation.current++; };
  }, [...dependencies, version]);
  return { ...state, refresh: () => setVersion(value => value + 1), more: () => load(state.next, generation.current) };
}
